"use client";

import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import { type QuickChoiceSpec, normalizeAnswer } from "@/lib/whiteboard/spec";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { WhiteboardFocusSurface, WhiteboardProgressiveReveal } from "./WhiteboardPresentation";
import { WhiteboardWriteInText } from "./WhiteboardWriteInText";
import {
  type WhiteboardCardEventHandler,
  getWhiteboardMarkClass,
  getWhiteboardTargetMark,
  isWhiteboardTargetHighlighted,
  simpleWhiteboardTargetId,
} from "./types";

interface QuickChoiceCardProps {
  spec: QuickChoiceSpec;
  renderState: WhiteboardRenderState;
  onEvent?: WhiteboardCardEventHandler;
}

export const QuickChoiceCard: React.FC<QuickChoiceCardProps> = ({ spec, renderState, onEvent }) => {
  const [pickedOption, setPickedOption] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [wrongOptions, setWrongOptions] = useState<Set<string>>(new Set());

  const hasCorrectAnswer = typeof spec.answerIndex === "number";
  const normalizedOptions = useMemo(() => spec.options.map(normalizeAnswer), [spec.options]);
  const correctOption = hasCorrectAnswer ? spec.options[spec.answerIndex ?? -1] : undefined;
  const validPickedOption =
    pickedOption && normalizedOptions.includes(normalizeAnswer(pickedOption)) ? pickedOption : null;
  const solved =
    validPickedOption !== null &&
    (!hasCorrectAnswer ||
      normalizeAnswer(validPickedOption) === normalizeAnswer(correctOption ?? ""));
  const questionTargetId = simpleWhiteboardTargetId("quick_choice", "question");
  const questionMark = getWhiteboardTargetMark(spec.question, renderState, questionTargetId);

  useEffect(() => {
    setPickedOption(null);
    setWrongOptions(new Set());
    setAttempt(0);
  }, [spec.id]);

  useEffect(() => {
    setPickedOption((current) =>
      current && normalizedOptions.includes(normalizeAnswer(current)) ? current : null
    );
    setWrongOptions((current) => {
      const next = new Set(
        [...current].filter((option) => normalizedOptions.includes(normalizeAnswer(option)))
      );
      return next.size === current.size ? current : next;
    });
  }, [normalizedOptions]);

  const handlePick = (index: number) => {
    if (solved) return;
    const option = spec.options[index];
    const correct = hasCorrectAnswer
      ? normalizeAnswer(option) === normalizeAnswer(correctOption ?? "")
      : undefined;
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);
    setPickedOption(option);
    if (correct === false) {
      setWrongOptions((prev) => new Set(prev).add(option));
    }
    onEvent?.({
      event: "answered",
      response: option,
      correct,
      action: hasCorrectAnswer ? "selected_answer" : "selected_direction",
      target: spec.question,
      targetId: simpleWhiteboardTargetId("quick_choice", "option", index),
      expected: hasCorrectAnswer ? correctOption : undefined,
      resolved: correct !== false,
      attempt: nextAttempt,
    });
  };

  return (
    <div className="space-y-4">
      <WhiteboardFocusSurface resolved={solved}>
        <WhiteboardWriteInText
          as="p"
          text={spec.question}
          data-whiteboard-target-id={questionTargetId}
          className={`text-lg sm:text-xl font-semibold leading-snug text-[#F9F9F9] ${
            isWhiteboardTargetHighlighted(spec.question, renderState, questionTargetId)
              ? "rounded-xl bg-[#FD954F]/10 px-2 py-1 ring-1 ring-[#FD954F]/30"
              : ""
          } ${getWhiteboardMarkClass(questionMark)}`}
        />
      </WhiteboardFocusSurface>

      <div className="whiteboard-control-reveal grid grid-cols-1 gap-2 sm:grid-cols-2">
        {spec.options.map((option, index) => {
          const normalizedOption = normalizeAnswer(option);
          const isWrong = [...wrongOptions].some(
            (wrongOption) => normalizeAnswer(wrongOption) === normalizedOption
          );
          const isCorrectPick = hasCorrectAnswer
            ? solved && normalizeAnswer(correctOption ?? "") === normalizedOption
            : false;
          const isSteeringPick =
            !hasCorrectAnswer &&
            validPickedOption !== null &&
            normalizeAnswer(validPickedOption) === normalizedOption;
          const optionTargetId = simpleWhiteboardTargetId("quick_choice", "option", index);
          const isHighlighted = isWhiteboardTargetHighlighted(option, renderState, optionTargetId);
          const mark = getWhiteboardTargetMark(option, renderState, optionTargetId);
          return (
            <motion.button
              key={`${index}-${option}`}
              whileTap={{ scale: 0.96 }}
              onClick={() => handlePick(index)}
              disabled={solved || isWrong}
              data-whiteboard-target-id={optionTargetId}
              className={`rounded-xl border px-4 py-3 text-left text-base font-semibold transition-colors duration-150 ${
                isCorrectPick || isSteeringPick
                  ? "border-[#22C55E]/60 bg-[#22C55E]/20 text-[#4ADE80]"
                  : isWrong
                    ? "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#FCA5A5] opacity-60"
                    : isHighlighted
                      ? "whiteboard-tutor-highlight-pulse border-[#FD954F]/80 bg-[#FD954F]/20 text-[#FFD9B8]"
                      : "border-white/15 bg-white/[0.06] text-[#F9F9F9] hover:border-[#FD954F]/60 hover:bg-[#FD954F]/10"
              } ${getWhiteboardMarkClass(mark)} ${
                solved && !isCorrectPick && !isSteeringPick ? "opacity-40" : ""
              }`}
              title={mark?.label}
            >
              {option}
            </motion.button>
          );
        })}
      </div>

      <WhiteboardProgressiveReveal show={solved && Boolean(spec.explanation)}>
        <p className="border-t border-white/10 pt-3 text-sm leading-relaxed text-[#D1D1D6]">
          {spec.explanation}
        </p>
      </WhiteboardProgressiveReveal>
    </div>
  );
};
